import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AddIcon from "@mui/icons-material/Add";
import BusinessIcon from "@mui/icons-material/Business";
import CallIcon from "@mui/icons-material/Call";
import EditIcon from "@mui/icons-material/Edit";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SyncIcon from "@mui/icons-material/Sync";
import { toast } from "react-toastify";

import DistributionNetworkApi from "../../../../../infrastructure/api/distributionNetworkApi";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";

const statusColor = (status) => {
  if (status === 2) return "success";
  if (status === 3) return "default";
  return "warning";
};

const initials = (name) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "؟";
  return parts
    .slice(0, 2)
    .map((p) => p[0])
    .join("");
};

const MetaLine = ({ icon, children, href }) => {
  if (!children) return null;
  const content = (
    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
      <Box sx={{ color: "text.secondary", mt: "2px", display: "flex" }}>{icon}</Box>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ wordBreak: "break-word", lineHeight: 1.6 }}
      >
        {children}
      </Typography>
    </Stack>
  );
  if (!href) return content;
  return (
    <MuiLink href={href} underline="hover" color="inherit" sx={{ display: "block" }}>
      {content}
    </MuiLink>
  );
};

const ContactCard = ({ contact, onEdit }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 1.5,
        py: 1.25,
        borderRadius: 1.5,
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        border: "1px solid",
        borderColor: "divider",
        transition: "border-color .15s, background-color .15s",
        "&:hover": {
          borderColor: alpha(theme.palette.primary.main, 0.35),
          bgcolor: alpha(theme.palette.primary.main, 0.06),
        },
      }}
    >
      <Avatar
        sx={{
          width: 40,
          height: 40,
          fontSize: 14,
          fontWeight: 700,
          bgcolor: alpha(theme.palette.primary.main, 0.12),
          color: "primary.main",
        }}
      >
        {initials(contact.fullName)}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" fontWeight={700} noWrap>
            {contact.fullName}
          </Typography>
          {contact.contactTypeTitle ? (
            <Chip size="small" label={contact.contactTypeTitle} sx={{ height: 22 }} />
          ) : null}
        </Stack>
        {contact.position ? (
          <Typography variant="caption" color="text.secondary" display="block" noWrap>
            {contact.position}
          </Typography>
        ) : null}
        <Stack direction="row" spacing={1.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
          {contact.mobile ? (
            <MuiLink href={`tel:${contact.mobile}`} variant="caption" underline="hover">
              {contact.mobile}
            </MuiLink>
          ) : null}
          {contact.phone ? (
            <MuiLink href={`tel:${contact.phone}`} variant="caption" underline="hover" color="text.secondary">
              {contact.phone}
            </MuiLink>
          ) : null}
          {contact.email ? (
            <MuiLink href={`mailto:${contact.email}`} variant="caption" underline="hover" color="text.secondary">
              {contact.email}
            </MuiLink>
          ) : null}
        </Stack>
      </Box>
      <Stack direction="row" spacing={0.25}>
        {contact.mobile ? (
          <Tooltip title="تماس">
            <IconButton size="small" component="a" href={`tel:${contact.mobile}`} color="primary">
              <CallIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
        <Tooltip title="ویرایش">
          <IconButton size="small" onClick={() => onEdit(contact.id)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
};

const EmptyHint = ({ title, actionLabel, onAction }) => (
  <Box
    sx={{
      py: 2.5,
      px: 2,
      textAlign: "center",
      borderRadius: 1.5,
      border: "1px dashed",
      borderColor: "divider",
      bgcolor: "grey.50",
    }}
  >
    <Typography variant="body2" color="text.secondary" sx={{ mb: onAction ? 1.25 : 0 }}>
      {title}
    </Typography>
    {onAction ? (
      <Button size="small" startIcon={<AddIcon />} onClick={onAction} variant="outlined">
        {actionLabel}
      </Button>
    ) : null}
  </Box>
);

const AreaNode = ({ area, contacts, companyId, defaultOpen, detailFilter, onNavigate }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(defaultOpen);
  const filteredContacts = useMemo(() => {
    if (!detailFilter) return contacts;
    const q = detailFilter.toLowerCase();
    return contacts.filter((c) =>
      `${c.fullName || ""} ${c.position || ""} ${c.mobile || ""} ${c.contactTypeTitle || ""}`
        .toLowerCase()
        .includes(q)
    );
  }, [contacts, detailFilter]);

  if (detailFilter && !filteredContacts.length) {
    const areaHit = `${area.name || ""} ${area.code || ""} ${area.countyName || ""} ${area.city || ""}`
      .toLowerCase()
      .includes(detailFilter.toLowerCase());
    if (!areaHit) return null;
  }

  return (
    <Box
      sx={{
        position: "relative",
        pr: 2,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 18,
          bottom: 12,
          right: 11,
          width: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.18),
          borderRadius: 1,
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "stretch",
          gap: 1.25,
          mb: 1,
        }}
      >
        <Box
          onClick={() => setOpen((v) => !v)}
          sx={{
            width: 24,
            height: 24,
            mt: 1.25,
            borderRadius: "50%",
            bgcolor: open ? "primary.main" : "background.paper",
            border: "2px solid",
            borderColor: "primary.main",
            color: open ? "primary.contrastText" : "primary.main",
            display: "grid",
            placeItems: "center",
            cursor: "pointer",
            flexShrink: 0,
            zIndex: 1,
            boxShadow: open ? `0 0 0 4px ${alpha(theme.palette.primary.main, 0.12)}` : "none",
          }}
        >
          {open ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
        </Box>

        <Box
          sx={{
            flex: 1,
            borderRadius: 2,
            border: "1px solid",
            borderColor: open ? alpha(theme.palette.primary.main, 0.35) : "divider",
            bgcolor: "background.paper",
            overflow: "hidden",
            boxShadow: open ? `0 8px 24px ${alpha(theme.palette.common.black, 0.06)}` : "none",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
            spacing={1}
            sx={{
              px: 1.75,
              py: 1.25,
              cursor: "pointer",
              bgcolor: open ? alpha(theme.palette.primary.main, 0.04) : "transparent",
            }}
            onClick={() => setOpen((v) => !v)}
          >
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <PlaceOutlinedIcon fontSize="small" color="primary" />
                <Typography fontWeight={700}>{area.name}</Typography>
                <Chip
                  size="small"
                  label={area.statusTitle || "—"}
                  color={statusColor(area.status)}
                  variant="outlined"
                  sx={{ height: 22 }}
                />
                <Chip
                  size="small"
                  icon={<PersonOutlineIcon />}
                  label={`${contacts.length} مخاطب`}
                  sx={{ height: 22 }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.35, display: "block" }}>
                {[area.code && `کد ${area.code}`, area.countyName, area.city]
                  .filter(Boolean)
                  .join(" · ") || "بدون مشخصات جغرافیایی راهنما"}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
              <Tooltip title="افزودن مخاطب">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() =>
                    onNavigate(
                      `/SLN/Management/DistributionContacts/create?companyId=${companyId}&areaId=${area.id}`
                    )
                  }
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="ویرایش امور">
                <IconButton
                  size="small"
                  onClick={() => onNavigate(`/SLN/Management/DistributionAreas/${area.id}`)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          <Collapse in={open}>
            <Divider />
            <Box sx={{ p: 1.75 }}>
              <Stack spacing={1} sx={{ mb: contacts.length || filteredContacts.length ? 1.5 : 0 }}>
                <MetaLine icon={<PersonOutlineIcon fontSize="small" />}>
                  {area.managerName ? `مدیر: ${area.managerName}` : null}
                </MetaLine>
                <MetaLine
                  icon={<CallIcon fontSize="small" />}
                  href={area.managerPhone ? `tel:${area.managerPhone}` : undefined}
                >
                  {area.managerPhone}
                </MetaLine>
                <MetaLine
                  icon={<CallIcon fontSize="small" />}
                  href={area.phone ? `tel:${area.phone}` : undefined}
                >
                  {area.phone && area.phone !== area.managerPhone ? area.phone : null}
                </MetaLine>
                <MetaLine icon={<LocationOnOutlinedIcon fontSize="small" />}>
                  {area.address}
                </MetaLine>
              </Stack>

              {!filteredContacts.length ? (
                <EmptyHint
                  title="مخاطبی برای این امور ثبت نشده است"
                  actionLabel="افزودن مخاطب"
                  onAction={() =>
                    onNavigate(
                      `/SLN/Management/DistributionContacts/create?companyId=${companyId}&areaId=${area.id}`
                    )
                  }
                />
              ) : (
                <Stack spacing={1}>
                  {filteredContacts.map((c) => (
                    <ContactCard
                      key={c.id}
                      contact={c}
                      onEdit={(id) => onNavigate(`/SLN/Management/DistributionContacts/${id}`)}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          </Collapse>
        </Box>
      </Box>
    </Box>
  );
};

const DistributionNetworkExplorerPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const companyIdParam = searchParams.get("companyId");

  const [companies, setCompanies] = useState([]);
  const [term, setTerm] = useState("");
  const [detailFilter, setDetailFilter] = useState("");
  const [listLoading, setListLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(
    companyIdParam ? Number(companyIdParam) : null
  );
  const [hierarchy, setHierarchy] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const loadCompanies = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await DistributionNetworkApi.getCompanies(true);
      if (!res.ok) {
        toast.error(res.message || "خطا در دریافت شرکت‌ها");
        setCompanies([]);
        return;
      }
      setCompanies(res.data || []);
    } finally {
      setListLoading(false);
    }
  }, []);

  const loadHierarchy = useCallback(async (companyId) => {
    if (!companyId) {
      setHierarchy(null);
      return;
    }
    setDetailLoading(true);
    try {
      const res = await DistributionNetworkApi.getCompanyHierarchy(companyId);
      if (!res.ok) {
        toast.error(res.message || "خطا در دریافت سلسله‌مراتب");
        setHierarchy(null);
        return;
      }
      setHierarchy(res.data);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    if (selectedId) loadHierarchy(selectedId);
  }, [selectedId, loadHierarchy]);

  const filteredCompanies = useMemo(() => {
    if (!term) return companies;
    const q = term.toLowerCase();
    return companies.filter((c) =>
      `${c.name || ""} ${c.shortName || ""} ${c.province || ""}`
        .toLowerCase()
        .includes(q)
    );
  }, [companies, term]);

  const selectCompany = (id) => {
    setSelectedId(id);
    setDetailFilter("");
    setSearchParams(id ? { companyId: String(id) } : {});
  };

  const company = hierarchy?.company || hierarchy?.Company;
  const areas = hierarchy?.areas || hierarchy?.Areas || [];
  const companyContacts =
    hierarchy?.companyContacts || hierarchy?.CompanyContacts || [];

  const filteredCompanyContacts = useMemo(() => {
    if (!detailFilter) return companyContacts;
    const q = detailFilter.toLowerCase();
    return companyContacts.filter((c) =>
      `${c.fullName || ""} ${c.position || ""} ${c.mobile || ""} ${c.contactTypeTitle || ""}`
        .toLowerCase()
        .includes(q)
    );
  }, [companyContacts, detailFilter]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <BreadcrumbsComponent />

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          px: 0.5,
        }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <AccountTreeIcon color="primary" />
            <Typography variant="h6" fontWeight={800}>
              شبکه توزیع
            </Typography>
            <Chip size="small" label="کارتابل کارشناس" color="primary" variant="outlined" />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            شرکت را انتخاب کنید تا امور و مخاطبین به‌صورت درختی نمایش داده شود
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            component={Link}
            to="/SLN/Management/DistributionCompanies"
          >
            مدیریت لیست‌ها
          </Button>
          <Tooltip title="بروزرسانی لیست">
            <IconButton color="primary" onClick={loadCompanies} disabled={listLoading}>
              <SyncIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "300px 1fr", lg: "320px 1fr" },
          gap: 2,
          alignItems: "start",
          minHeight: "70vh",
        }}
      >
        {/* Company rail */}
        <Box
          sx={{
            position: { md: "sticky" },
            top: { md: 88 },
            borderRadius: 2.5,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            overflow: "hidden",
            boxShadow: `0 10px 30px ${alpha(theme.palette.common.black, 0.04)}`,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.04) }}
          >
            <Box>
              <Typography fontWeight={800}>شرکت‌های توزیع</Typography>
              <Typography variant="caption" color="text.secondary">
                {filteredCompanies.length} مورد
              </Typography>
            </Box>
            <Tooltip title="ایجاد شرکت">
              <IconButton
                size="small"
                color="primary"
                component={Link}
                to="/SLN/Management/DistributionCompanies/create"
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          <Box sx={{ px: 1.5, pb: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="جستجوی شرکت یا استان..."
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Divider />
          <Box sx={{ maxHeight: { xs: 320, md: "calc(70vh - 120px)" }, overflow: "auto", p: 1 }}>
            {listLoading ? (
              <Box display="flex" justifyContent="center" py={5}>
                <CircularProgress size={26} />
              </Box>
            ) : !filteredCompanies.length ? (
              <Typography variant="body2" color="text.secondary" align="center" py={4}>
                شرکتی یافت نشد
              </Typography>
            ) : (
              <Stack spacing={0.75}>
                {filteredCompanies.map((c) => {
                  const selected = selectedId === c.id;
                  return (
                    <Box
                      key={c.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => selectCompany(c.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") selectCompany(c.id);
                      }}
                      sx={{
                        cursor: "pointer",
                        borderRadius: 2,
                        px: 1.5,
                        py: 1.25,
                        border: "1px solid",
                        borderColor: selected
                          ? alpha(theme.palette.primary.main, 0.45)
                          : "transparent",
                        bgcolor: selected
                          ? alpha(theme.palette.primary.main, 0.08)
                          : "transparent",
                        borderRightWidth: selected ? 3 : 1,
                        borderRightColor: selected ? "primary.main" : undefined,
                        transition: "all .15s",
                        "&:hover": {
                          bgcolor: selected
                            ? alpha(theme.palette.primary.main, 0.1)
                            : alpha(theme.palette.action.hover, 0.6),
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={selected ? 800 : 600}
                        sx={{ lineHeight: 1.45 }}
                      >
                        {c.name}
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ mt: 0.75 }}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                      >
                        {c.province ? (
                          <Typography variant="caption" color="text.secondary">
                            {c.province}
                          </Typography>
                        ) : null}
                        <Typography variant="caption" color="text.disabled">
                          ·
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {c.areaCount || 0} امور
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          ·
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {c.contactCount || 0} مخاطب
                        </Typography>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>
        </Box>

        {/* Detail pane */}
        <Box
          sx={{
            borderRadius: 2.5,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            minHeight: 480,
            overflow: "hidden",
            boxShadow: `0 10px 30px ${alpha(theme.palette.common.black, 0.04)}`,
          }}
        >
          {!selectedId ? (
            <Box
              sx={{
                height: 480,
                display: "grid",
                placeItems: "center",
                textAlign: "center",
                px: 3,
                background: `radial-gradient(circle at top, ${alpha(
                  theme.palette.primary.main,
                  0.08
                )}, transparent 55%)`,
              }}
            >
              <Box>
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    mx: "auto",
                    mb: 2,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: "primary.main",
                  }}
                >
                  <BusinessIcon sx={{ fontSize: 34 }} />
                </Box>
                <Typography fontWeight={800} gutterBottom>
                  هنوز شرکتی انتخاب نشده
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  از ستون سمت راست یک شرکت توزیع را انتخاب کنید تا ساختار سلسله‌مراتبی آن باز شود
                </Typography>
              </Box>
            </Box>
          ) : detailLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={420}>
              <CircularProgress />
            </Box>
          ) : !company ? (
            <Box p={3}>
              <Typography color="error">اطلاعات شرکت یافت نشد</Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  px: { xs: 2, md: 2.5 },
                  pt: 2.25,
                  pb: 2,
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.primary.main,
                    0.08
                  )} 0%, ${alpha(theme.palette.primary.main, 0.02)} 55%, transparent 100%)`,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", md: "flex-start" }}
                  spacing={1.5}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: "primary.main",
                        fontWeight: 800,
                      }}
                    >
                      <BusinessIcon />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.3 }}>
                          {company.name}
                        </Typography>
                        <Chip
                          size="small"
                          label={company.statusTitle || "—"}
                          color={statusColor(company.status)}
                        />
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                        {[company.shortName, company.province].filter(Boolean).join(" · ") ||
                          "بدون نام کوتاه / استان"}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1.25 }} flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={`${areas.length} امور برق`} variant="outlined" />
                        <Chip
                          size="small"
                          label={`${companyContacts.length} مخاطب شرکت`}
                          variant="outlined"
                        />
                        <Chip
                          size="small"
                          label={`${
                            companyContacts.length +
                            areas.reduce(
                              (sum, n) => sum + ((n.contacts || n.Contacts || []).length || 0),
                              0
                            )
                          } مخاطب کل`}
                          variant="outlined"
                        />
                      </Stack>
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() =>
                        navigate(
                          `/SLN/Management/DistributionAreas/create?companyId=${company.id}`
                        )
                      }
                    >
                      امور جدید
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PersonOutlineIcon />}
                      onClick={() =>
                        navigate(
                          `/SLN/Management/DistributionContacts/create?companyId=${company.id}`
                        )
                      }
                    >
                      مخاطب جدید
                    </Button>
                    <Tooltip title="بروزرسانی">
                      <IconButton size="small" onClick={() => loadHierarchy(company.id)}>
                        <SyncIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="بیشتر">
                      <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={menuAnchor}
                      open={Boolean(menuAnchor)}
                      onClose={() => setMenuAnchor(null)}
                    >
                      <MenuItem
                        onClick={() => {
                          setMenuAnchor(null);
                          navigate(`/SLN/Management/DistributionCompanies/${company.id}`);
                        }}
                      >
                        <EditIcon fontSize="small" sx={{ ml: 1 }} />
                        ویرایش شرکت
                      </MenuItem>
                      <MenuItem
                        component={Link}
                        to="/SLN/Management/DistributionAreas"
                        onClick={() => setMenuAnchor(null)}
                      >
                        لیست امور
                      </MenuItem>
                      <MenuItem
                        component={Link}
                        to="/SLN/Management/DistributionContacts"
                        onClick={() => setMenuAnchor(null)}
                      >
                        لیست مخاطبین
                      </MenuItem>
                    </Menu>
                  </Stack>
                </Stack>

                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.common.white, 0.7),
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 1.25,
                    }}
                  >
                    <MetaLine
                      icon={<CallIcon fontSize="small" />}
                      href={company.phone ? `tel:${company.phone}` : undefined}
                    >
                      {company.phone}
                    </MetaLine>
                    <MetaLine icon={<PersonOutlineIcon fontSize="small" />}>
                      {company.managerName
                        ? `مدیر: ${company.managerName}${
                            company.managerPhone ? ` — ${company.managerPhone}` : ""
                          }`
                        : null}
                    </MetaLine>
                    <MetaLine
                      icon={<EmailOutlinedIcon fontSize="small" />}
                      href={company.email ? `mailto:${company.email}` : undefined}
                    >
                      {company.email}
                    </MetaLine>
                    <MetaLine icon={<LocationOnOutlinedIcon fontSize="small" />}>
                      {company.address}
                    </MetaLine>
                  </Box>
                </Box>

                <TextField
                  fullWidth
                  size="small"
                  placeholder="فیلتر امور یا مخاطب در این شرکت..."
                  value={detailFilter}
                  onChange={(e) => setDetailFilter(e.target.value)}
                  sx={{ mt: 1.75, bgcolor: "background.paper", borderRadius: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box sx={{ p: { xs: 2, md: 2.5 } }}>
                <Stack spacing={3}>
                  <Box>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 1.25 }}
                    >
                      <Typography fontWeight={800}>مخاطبین سطح شرکت</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {filteredCompanyContacts.length} نفر
                      </Typography>
                    </Stack>
                    {!filteredCompanyContacts.length ? (
                      <EmptyHint
                        title={
                          detailFilter
                            ? "مخاطبی با این فیلتر در سطح شرکت نیست"
                            : "مخاطب مستقیمی در سطح شرکت ثبت نشده"
                        }
                        actionLabel={!detailFilter ? "افزودن مخاطب شرکت" : undefined}
                        onAction={
                          !detailFilter
                            ? () =>
                                navigate(
                                  `/SLN/Management/DistributionContacts/create?companyId=${company.id}`
                                )
                            : undefined
                        }
                      />
                    ) : (
                      <Stack spacing={1}>
                        {filteredCompanyContacts.map((c) => (
                          <ContactCard
                            key={c.id}
                            contact={c}
                            onEdit={(id) =>
                              navigate(`/SLN/Management/DistributionContacts/${id}`)
                            }
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>

                  <Box>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 1.5 }}
                    >
                      <Typography fontWeight={800}>درخت امور برق</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {areas.length} گره
                      </Typography>
                    </Stack>

                    {!areas.length ? (
                      <EmptyHint
                        title="هنوز امری برای این شرکت ثبت نشده است"
                        actionLabel="ایجاد اولین امور"
                        onAction={() =>
                          navigate(
                            `/SLN/Management/DistributionAreas/create?companyId=${company.id}`
                          )
                        }
                      />
                    ) : (
                      <Box>
                        {areas.map((node, index) => {
                          const area = node.area || node.Area;
                          const contacts = node.contacts || node.Contacts || [];
                          if (!area) return null;
                          return (
                            <AreaNode
                              key={area.id}
                              area={area}
                              contacts={contacts}
                              companyId={company.id}
                              defaultOpen={areas.length <= 3 || index === 0}
                              detailFilter={detailFilter}
                              onNavigate={navigate}
                            />
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                </Stack>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DistributionNetworkExplorerPage;
